import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

import {
  fetchFinalQcFailureReasons,
  FINAL_QC_FAILURE_REASONS_QUERY_KEY,
  FINAL_QC_WIRELESS_CHECKS_QUERY_KEY,
  revertPassAndFailFinalQc,
  type PottingGrade,
} from "./finalQcApi";

export function FinalQcMistakenPassPage() {
  const queryClient = useQueryClient();
  const writesAllowed = useHubModuleWrite("manufacturing");
  const [serialsText, setSerialsText] = useState("");
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [customReason, setCustomReason] = useState("");
  const [notes, setNotes] = useState("");
  const [pottingGrade, setPottingGrade] = useState<PottingGrade | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const failureReasonsQuery = useQuery({
    queryKey: FINAL_QC_FAILURE_REASONS_QUERY_KEY,
    queryFn: fetchFinalQcFailureReasons,
  });

  const reasons = failureReasonsQuery.data?.rows ?? [];

  const issueText = useMemo(() => {
    const labels = reasons
      .filter((reason) => selectedReasons.includes(reason.value))
      .map((reason) => reason.label);
    const custom = customReason.trim();
    if (custom) labels.push(custom);
    return labels.sort((left, right) => left.localeCompare(right)).join("; ");
  }, [customReason, reasons, selectedReasons]);

  const revertMutation = useMutation({
    mutationFn: () =>
      revertPassAndFailFinalQc({
        serials: serialsText,
        issue: issueText,
        issue_notes: notes.trim() === "" ? null : notes.trim(),
        potting_grade: pottingGrade,
      }),
    onSuccess: (data) => {
      setErrorMessage(null);
      setSuccessMessage(
        `Updated ${data.count} device(s): ${data.serials.join(", ")}. Each unit was removed from its shipment, filed as a Final QC incident, moved to QC hold, and potting was marked Fail when a potting row exists.`,
      );
      setSerialsText("");
      setSelectedReasons([]);
      setCustomReason("");
      setNotes("");
      setPottingGrade(null);
      void queryClient.invalidateQueries({ queryKey: FINAL_QC_WIRELESS_CHECKS_QUERY_KEY });
    },
    onError: (mutationError: Error) => {
      setSuccessMessage(null);
      setErrorMessage(mutationError.message);
    },
  });

  const failureReasonRows = reasons;
  const submitDisabled =
    !writesAllowed || issueText.trim() === "" || serialsText.trim() === "" || revertMutation.isPending;

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <PageTitleWithHelp
          hoverHint={
            "Use when a device was passed from Final QC by mistake (it already sits on a shipment or on post-pass station 9 while still on a batch). This removes the shipment assignment, files a Wireless Final QC quality incident, clears batch and sales order links like Fail, moves the device to QC hold, and sets potting to Fail when applicable. If the device has never passed, use the normal Fail control on the Final QC page instead."
          }
        >
          Mistaken Final QC pass
        </PageTitleWithHelp>
        <p className="text-sm text-slate-600">
          <Link to="/manufacturing/final-qc" className="text-sky-700 underline">
            Back to Final QC
          </Link>
        </p>
      </header>

      {successMessage ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {successMessage}
        </div>
      ) : null}

      {errorMessage ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">{errorMessage}</div>
      ) : null}

      {!writesAllowed ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Your account is read-only for Manufacturing. This action cannot be submitted.
        </div>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Device label serials</CardTitle>
          <p className="text-sm text-slate-500">One per line or comma-separated. Blank lines are ignored.</p>
        </CardHeader>
        <CardBody>
          <TextArea
            value={serialsText}
            onChange={(event) => setSerialsText(event.target.value)}
            className="min-h-32 font-mono text-sm"
            placeholder={"02221289\n02221418"}
            disabled={!writesAllowed || revertMutation.isPending}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Failure cause</CardTitle>
          <p className="text-sm text-slate-500">Same reasons as the Final QC fail modal. Choose at least one.</p>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="max-h-72 space-y-2 overflow-y-auto rounded-md border border-slate-200 p-3">
            {failureReasonRows.map((reason) => (
              <label key={reason.value} className="flex items-start gap-2 rounded px-2 py-1 hover:bg-slate-50">
                <input
                  type="checkbox"
                  checked={selectedReasons.includes(reason.value)}
                  onChange={(event) => {
                    setSelectedReasons((current) =>
                      event.target.checked
                        ? [...current, reason.value]
                        : current.filter((value) => value !== reason.value),
                    );
                  }}
                  className="mt-1"
                  disabled={!writesAllowed || revertMutation.isPending}
                />
                <span>
                  <span className="block text-sm font-medium text-slate-900">{reason.label}</span>
                  {reason.description ? (
                    <span className="block text-xs text-slate-500">{reason.description}</span>
                  ) : null}
                </span>
              </label>
            ))}
            {failureReasonRows.length === 0 && !failureReasonsQuery.isLoading ? (
              <p className="text-sm text-slate-600">No failure reasons are configured.</p>
            ) : null}
            {failureReasonsQuery.isLoading ? (
              <p className="flex items-center gap-2 text-sm text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading reasons…
              </p>
            ) : null}
          </div>

          <div>
            <Label>Custom reason</Label>
            <Input
              value={customReason}
              onChange={(event) => setCustomReason(event.target.value)}
              className="mt-1"
              disabled={!writesAllowed || revertMutation.isPending}
            />
          </div>

          <div>
            <Label>Notes</Label>
            <TextArea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              className="mt-1 font-sans"
              disabled={!writesAllowed || revertMutation.isPending}
            />
          </div>

          <section className="space-y-2">
            <p className="text-sm font-semibold text-slate-900">Potting grade (optional)</p>
            <p className="text-xs text-slate-500">
              Leave unset to record potting as Fail. Choose A, B, or C only if you must store a passing letter while
              still filing the incident (unusual).
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={pottingGrade === null ? "primary" : "secondary"}
                onClick={() => setPottingGrade(null)}
                disabled={!writesAllowed || revertMutation.isPending}
              >
                Fail (default)
              </Button>
              {(["A", "B", "C"] satisfies PottingGrade[]).map((grade) => (
                <Button
                  key={grade}
                  type="button"
                  size="sm"
                  variant={pottingGrade === grade ? "primary" : "secondary"}
                  onClick={() => setPottingGrade(grade)}
                  disabled={!writesAllowed || revertMutation.isPending}
                >
                  {grade}
                </Button>
              ))}
            </div>
          </section>

          <div className="flex justify-end">
            <Button
              type="button"
              variant="danger"
              disabled={submitDisabled}
              isLoading={revertMutation.isPending}
              onClick={() => revertMutation.mutate()}
            >
              Revert pass and file incident
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
